import { defineStore } from 'pinia';
import { UAParser } from 'ua-parser-js';
import { useBroadcastChannel } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useMoveInventoryOptions } from '@/composables';
import { useMoveInventoryCategories } from '@/composables';
import { useAnalyticsStore } from './analytics';

export const useAppStore = defineStore('app', () => {
    const router = useRouter();
    const hasReadParams = ref(false);
    const viewKey = ref(0);
    const ready = ref(false);
    const loading = ref(true);
    const paymentModalOpen = ref(false);
    const paymentModalOpenAsParent = ref(false);
    const bankDepositModalOpen = ref(false);
    const acceptInsuranceTermsModalOpen = ref(false);
    const darkMode = ref(false);
    const devMode = ref(false);
    const userAgent = ref(new UAParser());
    const tenant = ref(null);
    const predefined = ref({});
    const access_tags = ref([]);
    const direction = 'forward';
    const refundPolicy = ref(null);
    const informationAccuracy = ref(null);
    const termsAndConditions = ref(null);
    const companyBankAccount = ref(null);

    // Service settings
    const storage_enabled = ref(true);
    const packing_enabled = ref(true);
    const unpacking_enabled = ref(true);
    const packing_materials_enabled = ref(true);
    const pet_transport_enabled = ref(true);
    const vehicle_transport_enabled = ref(true);
    const final_payment_terms = ref(false);
    const auto_create_final = ref(true);
    const final_payment_days = ref(5);
    const deposit_hours = ref(2);
    const min_hours = ref(2);

    useMoveInventoryOptions();
    useMoveInventoryCategories();

    const { isSupported, post, data } = useBroadcastChannel({
        name: 'app-session',
    });

    // Show session expired if other tab is open
    watch(data, () => {
        if (data.value && isSupported.value && ready.value) {
            router.replace({ name: 'SessionExpired' });
        }
    });

    async function init() {
        // Post a message to other tabs
        if (isSupported.value) {
            post('init' + Math.random());
        }

        loading.value = true;

        if (!Object.values(predefined.value).length || !access_tags.value.length) {
            try {
                const [tenantResponse, predefinedResponse, accessTagsResponse] = await Promise.all([
                    fetchTenant(),
                    fetchPredefined(),
                    fetchAccessTags(),
                ]);

                tenant.value = tenantResponse.tenant;
                refundPolicy.value = tenantResponse.refund_policy;
                informationAccuracy.value = tenantResponse.information_accuracy;
                termsAndConditions.value = tenantResponse.terms_and_conditions;
                companyBankAccount.value = tenantResponse.company_bank_account;

                // Service settings
                storage_enabled.value = tenantResponse.storage_enabled ?? true;
                packing_enabled.value = tenantResponse.packing_enabled ?? true;
                unpacking_enabled.value = tenantResponse.unpacking_enabled ?? true;
                packing_materials_enabled.value = tenantResponse.packing_materials_enabled ?? true;
                pet_transport_enabled.value = tenantResponse.pet_transport_enabled ?? true;
                vehicle_transport_enabled.value = tenantResponse.vehicle_transport_enabled ?? true;
                final_payment_terms.value = tenantResponse.final_payment_terms ?? false;
                auto_create_final.value = tenantResponse.auto_create_final ?? true;
                final_payment_days.value = tenantResponse.final_payment_days ?? 5;
                deposit_hours.value = tenantResponse.deposit_hours;
                predefined.value = predefinedResponse.predefined;
                access_tags.value = accessTagsResponse;
                min_hours.value = tenantResponse.min_hours ?? tenantResponse.deposit_hours ?? min_hours.value;
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    router.replace({ name: 'PageNotFound' });
                }

                console.error(error);
            }

            if (tenant.value?.google_analytics_measurement_id) {
                console.log('📊 Google Analytics Measurement ID:', tenant.value.google_analytics_measurement_id);

                const analytics = useAnalyticsStore();
                analytics.addGoogleTagManager(tenant.value?.google_analytics_measurement_id);
            }
        }

        loading.value = false;
        ready.value = true;
    }

    async function fetchTenant() {
        let { data: tenantResponse } = await window.noAuthAxios.get('/customers/book/tenant');

        return tenantResponse;
    }

    async function fetchPredefined() {
        let { data: predefinedResponse } = await window.noAuthAxios.get('/customers/book/predefined');

        return predefinedResponse;
    }

    async function fetchAccessTags() {
        let {
            data: { data: accessTags },
        } = await window.noAuthAxios.get('/customers/book/access-tags');

        return accessTags;
    }

    // Toggle theme
    const toggleTheme = () => {
        darkMode.value = !darkMode.value;
    };

    const setLoading = () => {
        loading.value = true;
    };

    const completeLoading = () => {
        loading.value = false;
    };

    const openPaymentModal = (withBankDepositModel = false) => {
        paymentModalOpen.value = true;

        if (withBankDepositModel) {
            paymentModalOpenAsParent.value = true;
            bankDepositModalOpen.value = true;
        }
    };

    const closePaymentModal = () => {
        paymentModalOpen.value = false;
    };

    const getPaymentModalOpen = computed(() => paymentModalOpen.value);

    const openBankDepositModal = () => {
        bankDepositModalOpen.value = true;
    };

    const closeBankDepositModal = () => {
        bankDepositModalOpen.value = false;

        if (paymentModalOpenAsParent.value) {
            paymentModalOpenAsParent.value = false;
            paymentModalOpen.value = false;
        }
    };

    const getBankDepositModalOpen = computed(() => bankDepositModalOpen.value);

    const openAcceptInsuranceTermsModal = () => {
        acceptInsuranceTermsModalOpen.value = true;
    };

    const closeAcceptInsuranceTermsModal = () => {
        acceptInsuranceTermsModalOpen.value = false;
    };

    const isMobile = computed(() => userAgent.value.getDevice().type === 'mobile');

    const paymentSettings = computed(() => predefined.value?.payment_settings);

    const cardFeesEnabled = computed(() => paymentSettings.value?.card_fees_enabled);

    const tenantName = computed(() => tenant.value?.name);
    const tenantRefundPolicy = computed(() => refundPolicy.value);
    const tenantInformationAccuracy = computed(() => informationAccuracy.value);
    const tenantTerms = computed(() => termsAndConditions.value);
    const tenantBankAccount = computed(() => companyBankAccount.value);
    const tenantReviewLink = computed(() => tenant.value?.company?.settings?.review_link);
    const tenantPerHourPricing = computed(() => tenant.value?.company?.settings?.per_hour_pricing ?? false);
    const tenantCallOutFeeAsTime = computed(
        () => tenant.value?.company?.settings?.call_out_fee_as_factor_of_price ?? false,
    );
    const tenantFinalPaymentTerms = computed(() => final_payment_terms.value);
    const tenantAutoCreateFinal = computed(() => auto_create_final.value);
    const tenantFinalPaymentDays = computed(() => final_payment_days.value);
    const tenantDepositHours = computed(() => deposit_hours.value);
    const tenantMinHours = computed(() => min_hours.value);
    return {
        tenant,
        predefined,
        access_tags,
        viewKey,
        ready,
        loading,
        paymentModalOpen,
        acceptInsuranceTermsModalOpen,
        darkMode,
        devMode,
        userAgent,
        tenantName,
        tenantRefundPolicy,
        tenantInformationAccuracy,
        tenantTerms,
        tenantBankAccount,
        tenantReviewLink,
        tenantPerHourPricing,
        tenantCallOutFeeAsTime,
        tenantFinalPaymentTerms,
        tenantAutoCreateFinal,
        tenantFinalPaymentDays,
        tenantDepositHours,
        tenantMinHours,
        // Service settings
        storage_enabled,
        packing_enabled,
        unpacking_enabled,
        packing_materials_enabled,
        pet_transport_enabled,
        vehicle_transport_enabled,
        // Methods
        init,
        toggleTheme,
        setLoading,
        completeLoading,

        // Payment Modal
        openPaymentModal,
        closePaymentModal,

        // Bank Deposit Modal
        openBankDepositModal,
        closeBankDepositModal,

        // Accept Insurance Terms Modal
        openAcceptInsuranceTermsModal,
        closeAcceptInsuranceTermsModal,

        // Getters
        getPaymentModalOpen,
        getBankDepositModalOpen,
        isMobile,
        direction,
        hasReadParams,

        paymentSettings,
        cardFeesEnabled,
    };
});
